import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, AlertCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import HqFooter from "@/components/admin/HqFooter";
import usePageTitle from "@/hooks/usePageTitle";

/**
 * Hidden admin portal — non-standard URL `/hq-command-77`.
 *
 * Security layers:
 *   • Email allowlist (backend rejects non-staff/root with generic 401)
 *   • Brute-force throttle (5 fails / 15 min, backend-enforced)
 *   • No links from main app — chefs never see this URL
 *   • Stores the access token in Supabase's session so RLS bypass works
 *     immediately on /hq-command-77/users requests.
 */
export default function HqCommandLogin() {
  usePageTitle("pageTitles.adminLogin");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If you arrive at this URL already authenticated as admin, bounce straight in.
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res?.data?.role) navigate("/hq-command-77/users");
      } catch {
        /* not admin or no session — show form */
      }
    })();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: email.trim(),
        password,
      });
      // Plant the session into Supabase so subsequent .from() queries work.
      const { error: setErr } = await supabase.auth.setSession({
        access_token: res.data.access_token,
        refresh_token: res.data.refresh_token || "",
      });
      if (setErr) throw setErr;
      navigate("/hq-command-77/users");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        // Pydantic validation errors arrive as an array of { msg, loc, ... }
        msg = "Invalid email or password format";
      } else if (err?.message) {
        msg = "Invalid credentials";
      } else {
        msg = "Invalid credentials";
      }
      setError(msg);
      // Also toast for redundancy — some hidden viewport states swallow the inline error.
      try {
        const { toast } = await import("sonner");
        toast.error(msg);
      } catch { /* sonner not loaded — inline error covers it */ }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-slate-950"
      data-testid="hq-login-root"
    >
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-xl border border-emerald-500/20 bg-gradient-to-br from-slate-900 to-slate-950 p-7 shadow-2xl shadow-emerald-500/5">
        <div className="flex items-center gap-2.5 mb-1.5">
          <Shield className="h-5 w-5 text-emerald-400" />
          <h1 className="text-base font-bold text-white tracking-wide">
            HQ Command
          </h1>
        </div>
        <p className="text-[0.7rem] uppercase tracking-[0.25em] text-emerald-400/70 mb-6 font-mono">
          Internal staff portal · auth required
          <span className="block text-[0.55rem] text-emerald-500/40 mt-1 normal-case tracking-normal">
            build · hq-v2 · 2026-06-19
          </span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="bg-slate-950 border-slate-700 text-white"
              data-testid="hq-login-email"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="bg-slate-950 border-slate-700 text-white"
              data-testid="hq-login-password"
            />
          </div>

          {error && (
            <div
              role="alert"
              data-testid="hq-login-error"
              className="flex items-start gap-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2"
            >
              <AlertCircle className="h-3.5 w-3.5 flex-none mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2"
            data-testid="hq-login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Authenticating…
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Enter
              </>
            )}
          </Button>
        </form>

        <p className="mt-6 text-[0.65rem] text-slate-600 text-center font-mono">
          unauthorized access is logged + reported
        </p>
        </div>
      </div>

      <HqFooter />
    </div>
  );
}
